import { Card } from "@design";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SkillResponse } from "@/api";
import {
  addSkillTeamMember,
  addSkillUserMember,
  getSkillMembersOptions,
  getSkillMembersQueryKey,
  removeSkillTeamMember,
  removeSkillUserMember,
  updateSkillTeamMember,
  updateSkillUserMember,
} from "@/api";
import { type MembershipApi, MembershipPanel } from "@/components/build/MembershipPanel";
import { Text } from "@/components/Text";
import { useCurrentUser } from "@/features/account/useCurrentUser";

/**
 * Skills' Access tab: the membership model, via the shared MembershipPanel.
 * Platform-managed skills (system or extension-provided) are available to
 * everyone and have no members, so they get a note instead.
 */
export function SkillAccessTab({ skill }: Readonly<{ skill: SkillResponse }>) {
  const { t } = useTranslation("skills");
  if (skill.isSystem || skill.extension) {
    return (
      <Card>
        <Text variant="body-sm" tone="secondary">
          {t("access.platformManaged")}
        </Text>
      </Card>
    );
  }
  return <SkillMembershipPanel skill={skill} />;
}

function SkillMembershipPanel({ skill }: Readonly<{ skill: SkillResponse }>) {
  const me = useCurrentUser();

  const api = useMemo<MembershipApi>(
    () => ({
      membersOptions: () => getSkillMembersOptions({ path: { skill_uuid: skill.uuid } }),
      membersQueryKey: () => getSkillMembersQueryKey({ path: { skill_uuid: skill.uuid } }),
      addUser: (id, role) =>
        addSkillUserMember({ path: { skill_uuid: skill.uuid }, body: { userUuid: id, role }, throwOnError: true }),
      updateUserRole: (id, role) =>
        updateSkillUserMember({ path: { skill_uuid: skill.uuid, user_uuid: id }, body: { role }, throwOnError: true }),
      removeUser: (id) =>
        removeSkillUserMember({ path: { skill_uuid: skill.uuid, user_uuid: id }, throwOnError: true }),
      updateTeamRole: (id, role) =>
        updateSkillTeamMember({ path: { skill_uuid: skill.uuid, team_uuid: id }, body: { role }, throwOnError: true }),
      removeTeam: (id) =>
        removeSkillTeamMember({ path: { skill_uuid: skill.uuid, team_uuid: id }, throwOnError: true }),
      addTeam: (id, role) =>
        addSkillTeamMember({ path: { skill_uuid: skill.uuid }, body: { teamUuid: id, role }, throwOnError: true }),
    }),
    [skill.uuid],
  );

  return <MembershipPanel api={api} currentUserUuid={me.data?.user.uuid} />;
}
